import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check, ArrowRight, Zap } from 'lucide-react';
import { pricingPlans } from '../data/mock';
import { staggerCards } from '../utils/animations';

gsap.registerPlugin(ScrollTrigger);

const Pricing = () => {
  const headerRef = useRef(null);
  const plansRef = useRef(null);

  useEffect(() => {
    // Header animation
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Pricing cards animation
    if (plansRef.current) {
      const cards = plansRef.current.querySelectorAll('.pricing-card');
      staggerCards(cards);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="pricing-page pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <div ref={headerRef} className="text-center">
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            Transparent Pricing
          </span>
          <h1 className="font-headline text-5xl md:text-7xl font-bold tracking-headline uppercase mb-8">
            Choose Your <span className="text-[var(--primary)]">Plan</span>
          </h1>
          <p className="font-body text-[var(--on-surface-variant)] text-xl max-w-3xl mx-auto leading-relaxed">
            Investment in quality delivers exponential returns.
            <br />
            Choose a plan that matches your ambition.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section ref={plansRef} className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className={`pricing-card relative bg-[var(--surface-container-low)] p-8 lg:p-10 hover:bg-[var(--surface-container-high)] transition-all duration-500 ${
                plan.highlighted ? 'md:-mt-8 md:mb-0 border-2 border-[var(--primary)]' : ''
              }`}
            >
              {/* Top border */}
              <div className={`absolute top-0 left-0 w-full h-1 ${
                plan.highlighted ? 'bg-[var(--primary)]' : 'bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left'
              }`} />

              {/* Highlighted Badge */}
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[var(--primary)] text-[var(--on-primary)] font-headline text-xs font-bold uppercase tracking-code">
                  Most Popular
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-8">
                <h3 className="font-headline text-2xl font-bold uppercase mb-2">{plan.name}</h3>
                <p className="font-body text-[var(--on-surface-variant)] text-sm mb-6">{plan.tagline}</p>
                
                <div className="mb-4">
                  <span className="font-headline text-5xl font-bold text-[var(--primary)]">{plan.price}</span>
                  <span className="font-body text-[var(--on-surface-variant)] text-sm ml-2">{plan.priceDetail}</span>
                </div>

                <p className="font-body text-[var(--on-surface-variant)] text-sm leading-relaxed">
                  {plan.description}
                </p>
              </div>

              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="font-body text-[var(--on-surface-variant)] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Delivery Time */}
              <div className="mb-8 p-4 bg-[var(--surface-container)] border-l-2 border-[var(--primary)]">
                <div className="flex items-center gap-2 text-[var(--on-surface-variant)] text-sm">
                  <Zap size={16} className="text-[var(--primary)]" />
                  <span className="font-headline font-bold">Delivery:</span> {plan.deliveryTime}
                </div>
              </div>

              {/* CTA Button */}
              <Link
                to="/contact"
                className={`magnetic block w-full text-center px-8 py-4 font-headline font-bold text-sm uppercase tracking-code transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] hover:brightness-110'
                    : 'border-2 border-[var(--outline-variant)]/40 text-[var(--on-surface)] hover:border-[var(--primary)] hover:bg-[var(--surface-container)]'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[var(--surface-container-lowest)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-12 text-center">
            Frequently <span className="text-[var(--primary)]">Asked</span>
          </h2>

          <div className="space-y-6">
            {[
              {
                q: "What's included in the support period?",
                a: "Bug fixes, security updates, minor feature adjustments, and technical support via email and Slack."
              },
              {
                q: "Can I upgrade my plan later?",
                a: "Absolutely! You can upgrade at any time, and we'll credit your previous investment toward the new plan."
              },
              {
                q: "Do you offer payment plans?",
                a: "Yes, we offer milestone-based payments for Professional and Enterprise plans. Typical structure: 30% upfront, 40% at mid-point, 30% on completion."
              },
              {
                q: "What if I need custom features?",
                a: "The Enterprise plan is fully customizable. We'll work with you to scope and price any custom requirements."
              },
              {
                q: "Is hosting included?",
                a: "We provide deployment and initial hosting setup. Ongoing hosting costs depend on your infrastructure choice (AWS, GCP, etc.)."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-[var(--surface-container)] p-6 lg:p-8 hover:bg-[var(--surface-container-high)] transition-all duration-300"
              >
                <h3 className="font-headline text-lg font-bold mb-3">{faq.q}</h3>
                <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-8">
            Not Sure Which <span className="text-[var(--primary)]">Plan?</span>
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-12">
            Let's discuss your project requirements and find the perfect fit
          </p>
          <Link
            to="/contact"
            className="magnetic inline-flex items-center gap-2 bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none group"
          >
            Schedule Consultation
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
